import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { WarehouseService } from '../../../service/admin/warehouseService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useLoader } from '../../../base/context/LoaderProvider';
import Loader from '../../../component/common/loader';
import './style.css';

export default function ShippingManagement() {
  const message = useMessage();
  const loader = useLoader();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    phone: '',
    email: '',
    landmark: '',
    instructions: '',
    isActive: true,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const response = await WarehouseService.getAllWarehouses();
      if (response?.status?.code === 200) {
        setWarehouses(response.data || []);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to fetch warehouses', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
      message.showToast(error?.message || 'Error loading warehouses', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingWarehouse(null);
    setErrors({});
    setFormData({
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      phone: '',
      email: '',
      landmark: '',
      instructions: '',
      isActive: true,
    });
    setShowModal(true);
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setErrors({});
    setFormData({
      name: warehouse.name || '',
      address1: warehouse.address1 || '',
      address2: warehouse.address2 || '',
      city: warehouse.city || '',
      state: warehouse.state || '',
      pincode: warehouse.pincode || '',
      country: warehouse.country || 'India',
      phone: warehouse.phone || '',
      email: warehouse.email || '',
      landmark: warehouse.landmark || '',
      instructions: warehouse.instructions || '',
      isActive: warehouse.isActive !== undefined ? warehouse.isActive : true,
    });
    setShowModal(true);
  };

  const handleDeleteClick = (warehouse) => {
    setDeleteConfirm(warehouse);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    loader.showLoading(true);
    try {
      const response = await WarehouseService.deleteWarehouse(deleteConfirm._id);
      if (response?.status?.code === 200) {
        message.showToast('Warehouse deleted successfully', ToastTypes.SUCCESS);
        await fetchWarehouses();
        setDeleteConfirm(null);
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to delete warehouse', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to delete warehouse:', error);
      message.showToast(error?.message || 'Failed to delete warehouse', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    loader.showLoading(true);
    try {
      const response = await WarehouseService.toggleWarehouseActive(id);
      if (response?.status?.code === 200) {
        message.showToast('Warehouse status updated successfully', ToastTypes.SUCCESS);
        await fetchWarehouses();
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to update warehouse status', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to toggle warehouse status:', error);
      message.showToast(error?.message || 'Failed to update warehouse status', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Only validate email format if email is provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    loader.showLoading(true);
    try {
      let response;
      if (editingWarehouse) {
        response = await WarehouseService.updateWarehouse(editingWarehouse._id, formData);
      } else {
        response = await WarehouseService.createWarehouse(formData);
      }

      if (response?.status?.code === 200) {
        message.showToast(
          editingWarehouse ? 'Warehouse updated successfully' : 'Warehouse created successfully',
          ToastTypes.SUCCESS
        );
        setShowModal(false);
        await fetchWarehouses();
      } else if (response?.status?.code === 402) {
        window.location.href = "/login";
      } else {
        message.showToast(response?.error?.message || 'Failed to save warehouse', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Failed to save warehouse:', error);
      message.showToast(error?.message || 'Failed to save warehouse', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const formatAddress = (warehouse) => {
    const parts = [
      warehouse.address1,
      warehouse.address2,
      warehouse.city,
      warehouse.state,
      warehouse.pincode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="sm-container">
      <div className="sm-header">
        <button className="sm-add-btn" onClick={handleAddNew}>
          <FaPlus /> Add Warehouse
        </button>
      </div>

      <div className="sm-content-area" style={{ position: 'relative', minHeight: 120 }}>
        <Loader value={loading} variant="overlay" text="Loading warehouses..." />
      {warehouses.length === 0 ? (
        <div className="sm-empty-state">
          <FaMapMarkerAlt className="sm-empty-icon" />
          <h3>No warehouses yet</h3>
          <p>Add your first warehouse to enable self-pickup for customers</p>
          <button className="sm-add-btn" onClick={handleAddNew}>
            <FaPlus /> Add Warehouse
          </button>
        </div>
      ) : (
        <div className="sm-grid">
          {warehouses.map((warehouse) => (
            <div key={warehouse._id} className={`sm-card ${!warehouse.isActive ? 'sm-card-inactive' : ''}`}>
              <div className="sm-card-header">
                <div className="sm-card-title-section">
                  <h3 className="sm-card-title">{warehouse.name}</h3>
                  <span className={`sm-badge ${warehouse.isActive ? 'sm-badge-active' : 'sm-badge-inactive'}`}>
                    {warehouse.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="sm-card-actions">
                  <button
                    className={`sm-toggle-btn ${warehouse.isActive ? 'sm-toggle-btn-active' : 'sm-toggle-btn-inactive'}`}
                    onClick={() => handleToggleActive(warehouse._id)}
                  >
                    {warehouse.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="sm-action-btn sm-action-btn-edit"
                    onClick={() => handleEdit(warehouse)}
                    title="Edit"
                  >
                    <FaEdit size={12} />
                  </button>
                  <button
                    className="sm-action-btn sm-action-btn-delete"
                    onClick={() => handleDeleteClick(warehouse)}
                    title="Delete"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </div>
              <div className="sm-card-body">
                <div className="sm-card-info">
                  <FaMapMarkerAlt className="sm-info-icon" />
                  <div>
                    <p className="sm-info-text">{formatAddress(warehouse)}</p>
                    {warehouse.phone && (
                      <p className="sm-info-meta">Phone: {warehouse.phone}</p>
                    )}
                    {warehouse.landmark && (
                      <p className="sm-info-meta">Landmark: {warehouse.landmark}</p>
                    )}
                  </div>
                </div>
                {warehouse.instructions && (
                  <div className="sm-card-instructions">
                    <p className="sm-instructions-label">Pickup Instructions:</p>
                    <p className="sm-instructions-text">{warehouse.instructions}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="sm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="sm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sm-modal-header">
              <h2>{editingWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}</h2>
              <button className="sm-modal-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form className="sm-form" onSubmit={handleSubmit}>
              <div className="sm-form-section">
                <h3 className="sm-form-section-title">Basic Information</h3>
                <div className="sm-form-grid">
                  <div className="sm-form-group">
                    <label>Warehouse Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={errors.name ? 'sm-input-error' : ''}
                      placeholder="e.g., Main Warehouse"
                    />
                    {errors.name && <span className="sm-error-text">{errors.name}</span>}
                  </div>
                  <div className="sm-form-group">
                    <label>Phone Number</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="e.g., +91 9876543210"
                    />
                  </div>
                </div>
                <div className="sm-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={errors.email ? 'sm-input-error' : ''}
                    placeholder="warehouse@example.com"
                  />
                  {errors.email && <span className="sm-error-text">{errors.email}</span>}
                </div>
              </div>

              <div className="sm-form-section">
                <h3 className="sm-form-section-title">Address Details</h3>
                <div className="sm-form-group">
                  <label>Address Line 1</label>
                  <input
                    type="text"
                    value={formData.address1}
                    onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
                    className={errors.address1 ? 'sm-input-error' : ''}
                    placeholder="Street address, building number"
                  />
                  {errors.address1 && <span className="sm-error-text">{errors.address1}</span>}
                </div>
                <div className="sm-form-group">
                  <label>Address Line 2</label>
                  <input
                    type="text"
                    value={formData.address2}
                    onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                    placeholder="Apartment, suite, unit, etc."
                  />
                </div>
                <div className="sm-form-grid">
                  <div className="sm-form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className={errors.city ? 'sm-input-error' : ''}
                      placeholder="City"
                    />
                    {errors.city && <span className="sm-error-text">{errors.city}</span>}
                  </div>
                  <div className="sm-form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className={errors.state ? 'sm-input-error' : ''}
                      placeholder="State"
                    />
                    {errors.state && <span className="sm-error-text">{errors.state}</span>}
                  </div>
                </div>
                <div className="sm-form-grid">
                  <div className="sm-form-group">
                    <label>Pincode</label>
                    <input
                      type="text"
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      className={errors.pincode ? 'sm-input-error' : ''}
                      placeholder="e.g., 110001"
                    />
                    {errors.pincode && <span className="sm-error-text">{errors.pincode}</span>}
                  </div>
                  <div className="sm-form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="Country"
                    />
                  </div>
                </div>
                <div className="sm-form-group">
                  <label>Landmark</label>
                  <input
                    type="text"
                    value={formData.landmark}
                    onChange={(e) => setFormData({ ...formData, landmark: e.target.value })}
                    placeholder="Nearby landmark for easy location"
                  />
                </div>
              </div>

              <div className="sm-form-section">
                <h3 className="sm-form-section-title">Additional Information</h3>
                <div className="sm-form-group">
                  <label>Pickup Instructions</label>
                  <textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows="3"
                    placeholder="Instructions for customers picking up from this warehouse..."
                    className="sm-textarea"
                  />
                </div>
                <div className="sm-form-group">
                  <label className="sm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>Active (Show in checkout)</span>
                  </label>
                </div>
              </div>

              <div className="sm-form-actions">
                <button type="button" className="sm-btn sm-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="sm-btn sm-btn-primary">
                  {editingWarehouse ? 'Update' : 'Create'} Warehouse
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="sm-delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div className="sm-delete-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="sm-delete-confirm-icon">
              <FaTrash size={32} />
            </div>
            <h3 className="sm-delete-confirm-title">Delete Warehouse</h3>
            <p className="sm-delete-confirm-message">
              Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>?
            </p>
            <p className="sm-delete-confirm-warning">
              This action cannot be undone. All associated data will be permanently removed.
            </p>
            <div className="sm-delete-confirm-actions">
              <button 
                className="sm-delete-confirm-btn sm-delete-confirm-cancel"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="sm-delete-confirm-btn sm-delete-confirm-delete"
                onClick={handleDeleteConfirm}
              >
                <FaTrash size={12} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
